import { useEffect, useMemo } from 'react'
import { OpeningsDrawer, OrthographicWall } from '@structuralPlanningComponents'
import { useControlStore, useControlStoreApi, useEditElementStore } from '@editorStores'
import { useSceneElement } from '@editorHooks'

/**
 * Wires the "in Draufsicht bearbeiten" tool into the IFC import scene by injecting Structural
 * Planning's `OrthographicWall` (the single-wall elevation view) and `OpeningsDrawer` (the
 * draw/drag tool for openings on that wall's face) while the tool is active, mirroring
 * `StructuralPlanningScene`'s `draw-openings` mode. The IFC/GLB model is hidden while active so it
 * doesn't obscure the wall face; its previous visibility is restored on leave.
 *
 * `active` is whether the tool is switched on (`ifcMode === 'draw-openings'`); `wallGuid` is the
 * wall being edited (the active planar element) and `openingGuid` the opening currently selected
 * within it, if any.
 */
const useIFCImportOpeningDrawing = (
  active: boolean,
  wallGuid: string | null,
  openingGuid: string | null,
): void => {
  const controlStoreApi = useControlStoreApi()
  const setShowGltfModel = useControlStore(state => state.setShowGltfModel)
  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)

  const enabled = active && !!wallGuid

  useEffect(() => {
    if (!enabled) return

    const showGltfModelOnEnter = controlStoreApi.getState().showGltfModel
    setShowGltfModel(false)
    return () => setShowGltfModel(showGltfModelOnEnter)
  }, [enabled, controlStoreApi, setShowGltfModel])

  const orthographicWallProperties = useMemo(
    () => ({
      wallGuid: wallGuid ?? '',
      onClickOpening: setActiveOpening,
      selectedOpenings: openingGuid ? [openingGuid] : undefined,
    }),
    [wallGuid, openingGuid, setActiveOpening],
  )

  useSceneElement({
    enabled,
    properties: orthographicWallProperties,
    component: OrthographicWall,
  })

  const openingsDrawerProperties = useMemo(
    () => ({ wallGuid: wallGuid ?? '', openingGuid }),
    [wallGuid, openingGuid],
  )

  useSceneElement({
    enabled,
    properties: openingsDrawerProperties,
    component: OpeningsDrawer,
    // Remount per opening (its only other property is the wall), like `key={activeOpening}` in
    // structural planning: switching the opening cancels an unfinished drawing
    updateInPlace: false,
  })
}

export default useIFCImportOpeningDrawing
